import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { authButtonStyle } from '../../constants/authStyles';
import { fetchInventoryBootstrap } from '../../utils/inventoryBootstrapClient';
import { tl } from '../../utils/inlineI18n';
import {
  loadSocketConfig,
  useSocketConfig,
  stoneByOtherId,
  stoneImageUrl,
  stoneDisplayName,
  effectLabel,
} from '../../utils/socketConfig';
import SocketSlots from './SocketSlots';
import { buildPublicImageUrl } from '../inventory/inventoryBagGridUtils';
import './Sockets.css';

// Demirci içindeki "Taş Ustası": 3 aynı taşı birleştirme ve silah/zırha taş takma.
const SocketMaster = ({ username, setError, clearError }) => {
  const cfg = useSocketConfig();
  const [mode, setMode] = useState('insert');
  const [inv, setInv] = useState(null);
  const [catalog, setCatalog] = useState({});
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [selItemKey, setSelItemKey] = useState('');
  const [selSocket, setSelSocket] = useState(null);
  const [selStone, setSelStone] = useState(0);

  const load = useCallback(async () => {
    if (!username) return;
    setLoading(true);
    try {
      const res = await fetchInventoryBootstrap(username);
      if (res.data?.success) {
        setInv(res.data.inventory || {});
        setCatalog(res.data.catalog || {});
      }
    } catch {
      setError?.(tl('Envanter yüklenemedi', 'Could not load inventory'));
    } finally {
      setLoading(false);
    }
  }, [username, setError]);

  useEffect(() => {
    load();
    loadSocketConfig(true);
  }, [load]);

  const stones = useMemo(() => {
    const counts = {};
    (inv?.others || []).forEach((o) => {
      if (stoneByOtherId(cfg, o.otherId)) counts[o.otherId] = (counts[o.otherId] || 0) + (o.count || 1);
    });
    return Object.entries(counts)
      .map(([id, count]) => ({ id: Number(id), count, ...stoneByOtherId(cfg, Number(id)) }))
      .sort((a, b) => a.id - b.id);
  }, [inv, cfg]);

  const socketItems = useMemo(() => {
    const out = [];
    const add = (type, list, idKey, catKey) => {
      (list || []).forEach((it) => {
        if (!it.socketCount) return;
        const base = (catalog[catKey] || []).find((c) => c.id === it[idKey]) || {};
        out.push({ ...it, itemType: type, key: `${type}-${it.id}`, name: base.name || `#${it[idKey]}`, imageUrl: base.imageUrl });
      });
    };
    add('weapon', inv?.weapons, 'weaponId', 'weapons');
    add('armor', inv?.armors, 'armorId', 'armors');
    return out;
  }, [inv, catalog]);

  const selItem = socketItems.find((x) => x.key === selItemKey) || null;
  const rates = cfg?.rates;

  const showResult = (ok, text) => setMsg({ ok, text });

  const doCraft = async (otherId) => {
    if (busy) return;
    setBusy(true);
    clearError?.();
    try {
      const res = await axios.post('/api/sockets/craft', { otherId });
      if (res.data?.success) showResult(!!res.data.crafted, res.data.message);
      else showResult(false, res.data?.message || tl('İşlem başarısız', 'Failed'));
      await load();
    } catch {
      showResult(false, tl('İşlem başarısız', 'Failed'));
    } finally {
      setBusy(false);
    }
  };

  const doInsert = async () => {
    if (busy || !selItem || selSocket == null || !selStone) return;
    setBusy(true);
    clearError?.();
    try {
      const res = await axios.post('/api/sockets/insert', {
        itemType: selItem.itemType,
        userItemId: selItem.id,
        socketIndex: selSocket,
        otherId: selStone,
      });
      if (res.data?.success) showResult(!!res.data.inserted, res.data.message);
      else showResult(false, res.data?.message || tl('İşlem başarısız', 'Failed'));
      setSelSocket(null);
      setSelStone(0);
      await load();
    } catch {
      showResult(false, tl('İşlem başarısız', 'Failed'));
    } finally {
      setBusy(false);
    }
  };

  const stoneTile = (s, onClick, active, extra) => (
    <button
      key={s.id}
      type="button"
      className={`socket-stone-tile${active ? ' socket-stone-tile--active' : ''}`}
      onClick={onClick}
      disabled={busy}
      title={`${stoneDisplayName(cfg, s.stone, s.level)} — ${effectLabel(cfg, s.stone.effect)} +${s.stone.values[s.level]}`}
    >
      <img src={stoneImageUrl(s.stone, s.level)} alt="" />
      <span className="socket-stone-count">x{s.count}</span>
      <span className="socket-stone-name">{stoneDisplayName(cfg, s.stone, s.level)}</span>
      {extra}
    </button>
  );

  if (!cfg) return <p className="no-items">{tl('Yükleniyor…', 'Loading…')}</p>;

  return (
    <div className="socket-master">
      <div className="socket-master-tabs">
        <button type="button" className={`socket-mt${mode === 'insert' ? ' socket-mt--on' : ''}`} onClick={() => setMode('insert')}>
          {tl('Taş Tak', 'Insert Stone')}
        </button>
        <button type="button" className={`socket-mt${mode === 'craft' ? ' socket-mt--on' : ''}`} onClick={() => setMode('craft')}>
          {tl('Taş Birleştir (3→1)', 'Combine (3→1)')}
        </button>
      </div>

      {msg ? <p className={`socket-msg ${msg.ok ? 'socket-msg--ok' : 'socket-msg--fail'}`}>{msg.text}</p> : null}
      {loading && !inv ? <p className="no-items">{tl('Yükleniyor…', 'Loading…')}</p> : null}

      {mode === 'craft' ? (
        <div>
          <p className="socket-hint">
            {tl('Aynı taştan 3 tane bir üst seviyeye birleştirilir. Başarısız olursa taşlar kaybolur.', 'Combine 3 identical stones into the next level. On failure the stones are lost.')}
            {rates ? ` (${tl('Başarı', 'Success')}: %${rates.craftSuccessPercent})` : ''}
          </p>
          <div className="socket-stone-grid">
            {stones.length === 0 ? <p className="no-items">{tl('Hiç taşın yok.', 'You have no stones.')}</p> : null}
            {stones.map((s) => {
              const canCraft = s.count >= 3 && s.level < 3;
              return stoneTile(s, canCraft ? () => doCraft(s.id) : undefined, false, (
                <span className={`socket-stone-action${canCraft ? '' : ' socket-stone-action--off'}`}>
                  {s.level >= 3 ? tl('Kusursuz', 'Flawless') : canCraft ? tl('Birleştir', 'Combine') : '3/3'}
                </span>
              ));
            })}
          </div>
        </div>
      ) : (
        <div>
          <p className="socket-hint">
            {tl('Soketli bir silah/zırh seç, boş soketi seç, ardından taşı seç. Başarısız olursa taş kaybolur, eşya zarar görmez.', 'Pick a socketed weapon/armor, an empty socket, then a stone. On failure the stone is lost; the item is safe.')}
            {rates ? ` (${tl('Başarı', 'Success')}: %${rates.insertSuccessPercent})` : ''}
          </p>
          <div className="socket-item-list">
            {socketItems.length === 0 ? (
              <p className="no-items">{tl('Soketli eşyan yok. Soketler yeni düşen/alınan silah ve zırhlarda rastgele çıkar.', 'No socketed items. New weapons/armors may roll sockets.')}</p>
            ) : null}
            {socketItems.map((it) => (
              <button
                key={it.key}
                type="button"
                className={`socket-item${selItemKey === it.key ? ' socket-item--on' : ''}`}
                onClick={() => {
                  setSelItemKey(it.key);
                  setSelSocket(null);
                }}
              >
                {it.imageUrl ? <img src={buildPublicImageUrl(it.imageUrl)} alt="" /> : null}
                <span>{it.name}</span>
                <SocketSlots item={it} compact />
              </button>
            ))}
          </div>
          {selItem ? (
            <div className="socket-insert-panel">
              <strong>{selItem.name}</strong>
              <SocketSlots item={selItem} selectedIndex={selSocket} onSelectSocket={setSelSocket} />
              {selSocket != null ? (
                <>
                  <div className="socket-stone-grid">
                    {stones.length === 0 ? <p className="no-items">{tl('Hiç taşın yok.', 'You have no stones.')}</p> : null}
                    {stones.map((s) => stoneTile(s, () => setSelStone(s.id), selStone === s.id))}
                  </div>
                  <button
                    type="button"
                    className="auth-button village-button--full"
                    style={authButtonStyle}
                    disabled={busy || !selStone}
                    onClick={doInsert}
                  >
                    {busy ? tl('İşleniyor…', 'Processing…') : tl('Taşı Tak', 'Insert')}
                  </button>
                </>
              ) : (
                <p className="socket-hint">{tl('Boş bir sokete dokun.', 'Tap an empty socket.')}</p>
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default SocketMaster;
